import { OktaAuth } from "@okta/okta-auth-js";
import { config } from "@/config";

export interface Tokens {
  accessToken: string;
  idToken: string;
}

export interface AuthenticateResult {
  authenticated: boolean;
  error?: string;
  diagnostics?: string;
}

function resolveIssuer(): string {
  const base = config.oktaIssuer.trim().replace(/\/$/, "");
  if (!base) {
    return "https://renewables.okta.com/oauth2/default";
  }
  return base.includes("/oauth2/") ? base : `${base}/oauth2/default`;
}

function resolveRedirectUri(): string {
  return (
    config.redirectUrl.trim() || `${window.location.origin}/login/callback`
  );
}

const authClient = new OktaAuth({
  issuer: resolveIssuer(),
  clientId: config.oktaClientId,
  redirectUri: resolveRedirectUri(),
  scopes: ["openid", "profile", "email"],
  pkce: true,
});

const REDIRECT_GUARD_KEY = "okta:lastRedirectAt";
const AUTH_DIAGNOSTICS_KEY = "okta:lastAuthDiagnostics";

function buildDiagnostics(): string {
  return JSON.stringify(
    {
      issuer: config.oktaIssuer,
      normalizedIssuer: resolveIssuer(),
      clientId: config.oktaClientId,
      redirectUri: resolveRedirectUri(),
      origin: window.location.origin,
      path: window.location.pathname,
      time: new Date().toISOString(),
    },
    null,
    2,
  );
}

function readDiagnostics(): string | undefined {
  return window.sessionStorage.getItem(AUTH_DIAGNOSTICS_KEY) ?? undefined;
}

// Avoids infinite redirect loops if Okta keeps rejecting the session.
function canTriggerRedirect(): boolean {
  const now = Date.now();
  const last = Number(window.sessionStorage.getItem(REDIRECT_GUARD_KEY) ?? 0);
  if (!Number.isFinite(last) || now - last > 5000) {
    window.sessionStorage.setItem(REDIRECT_GUARD_KEY, String(now));
    return true;
  }
  return false;
}

/** Handles the PKCE redirect flow: parses callback, checks session, or triggers sign-in.
 *
 * Memoized as a module-level singleton: React 18 StrictMode mounts `AuthProvider` twice in dev,
 * which would call Okta's one-shot `token.parseFromUrl()` twice on `/login/callback` — the second
 * call throws and would leave the app stuck on the "redirecting…" screen. Running it exactly once
 * mirrors the legacy loader behaviour. */
let inFlight: Promise<AuthenticateResult> | null = null;
export const authenticate = (): Promise<AuthenticateResult> => {
  inFlight ??= runAuthenticate();
  return inFlight;
};

const runAuthenticate = async (): Promise<AuthenticateResult> => {
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get("error");
  if (oauthError) {
    const description =
      params.get("error_description") ?? "Unknown OAuth error";
    return {
      authenticated: false,
      error: `${oauthError}: ${description}`,
      diagnostics: readDiagnostics(),
    };
  }

  if (authClient.isLoginRedirect()) {
    try {
      const authData = await authClient.token.parseFromUrl();
      authClient.tokenManager.setTokens(authData.tokens);
      return { authenticated: true };
    } catch (error) {
      console.error("Okta callback parsing failed", error);
      return {
        authenticated: false,
        error:
          "Callback parsing failed. Check issuer/redirect URI and app assignment in Okta.",
        diagnostics: readDiagnostics(),
      };
    }
  }

  const isAuthenticated = await authClient.isAuthenticated({
    onExpiredToken: "renew",
  });
  if (isAuthenticated) {
    return { authenticated: true };
  }

  if (canTriggerRedirect()) {
    window.sessionStorage.setItem(AUTH_DIAGNOSTICS_KEY, buildDiagnostics());
    await authClient.signInWithRedirect();
  }

  return { authenticated: false };
};

export const getTokensSync = (): Tokens | undefined => {
  const accessToken = authClient.getAccessToken();
  const idToken = authClient.getIdToken();
  return accessToken && idToken ? { accessToken, idToken } : undefined;
};

export const logout = async (): Promise<void> => {
  authClient.tokenManager.clear();
  await authClient.signOut({ postLogoutRedirectUri: window.location.origin });
};

export default authClient;
